<script setup lang="ts">
import { onMounted, computed } from "vue";
import { useAppStore } from "@/ts/stores/app";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import HamburgerButton from "@/ts/components/elements/hamburger-button.vue";
import LanguageSwitch from "@/ts/components/elements/language-switch.vue";

const breakpoints = useBreakpoints(breakpointsTailwind);

const showPageLinks = breakpoints.greater("lg");
const showHamburgerButton = breakpoints.smaller("lg");

const appStore = useAppStore();

interface Props {
    centerLinks?: Array<{ class: string; target: string; title: string; url: string }>;
    rightLinks?: Array<{ class: string; target: string; title: string; url: string }>;
    translatedRoutes: { nl?: { relative_url: string; lang: string }; fr?: { relative_url: string; lang: string }; en?: { relative_url: string; lang: string } };
    firstSectionOverlap?: boolean;
}

const props = defineProps<Props>();

function currentlyViewing(url: string): boolean {
    return window.location.href.indexOf(url) >= 0;
}

onMounted(() => {
    let last_known_scroll_position = 0;
    let ticking = false;

    window.addEventListener("scroll", function () {
        last_known_scroll_position = window.scrollY;

        if (!ticking) {
            window.requestAnimationFrame(function () {
                if (last_known_scroll_position > 50) {
                    appStore.navigation.header.sticky = true;
                } else {
                    appStore.navigation.header.sticky = false;
                }

                ticking = false;
            });

            ticking = true;
        }
    });
});

const lang = computed(() => {
    return document.documentElement.lang;
});
</script>

<template>
    <transition name="fade" appear>
        <header ref="header" :class="[appStore.navigation.header.sticky ? 'sticky' : 'not-sticky', appStore.navigation.slideout.visible ? 'slideout-visible' : '', { 'py-2 md:py-8': firstSectionOverlap }, { 'py-2 md:py-6': !firstSectionOverlap }, { 'md:!py-6': appStore.navigation.header.sticky }]">
            <div class="container mx-auto">
                <div class="md:justify-center flex items-center">
                    <transition name="fade">
                        <a :href="`/${lang}`" class="mr-auto" v-if="!appStore.navigation.slideout.visible">
                            <svg-vue
                                icon="rima-trans"
                                :class="[
                                    'md:h-14 w-auto h-10',
                                    {
                                        'text-white': firstSectionOverlap,
                                        'text-primary': !firstSectionOverlap,
                                        '!text-primary': appStore.navigation.header.sticky,
                                    },
                                ]"
                            ></svg-vue>
                            <span class="sr-only">RIMA Trans</span>
                        </a>
                    </transition>
                    <transition name="fade">
                        <div v-if="showPageLinks && centerLinks" class="flex gap-8">
                            <a
                                v-for="page in centerLinks"
                                :href="page.url"
                                :class="[
                                    page.class,
                                    {
                                        // unstyled links
                                        'text-md transition-colors inline-flex items-center hover:text-tertiary': page.class === 'link-unstyled',
                                        '!text-tertiary': currentlyViewing(page.url) && page.class === 'link-unstyled',
                                        'text-white': firstSectionOverlap && !appStore.navigation.header.sticky && page.class === 'link-unstyled',
                                        'text-primary': (!firstSectionOverlap && !appStore.navigation.header.sticky && page.class === 'link-unstyled') || (appStore.navigation.header.sticky && page.class === 'link-unstyled'),
                                    },
                                ]"
                            >
                                {{ page.title }}
                                <svg-vue v-if="page.class.indexOf('btn') >= 0" class="w-4 h-auto ml-2" icon="icons/arrow-right"></svg-vue>
                                <span
                                    v-else-if="page.url.indexOf('jobs') >= 0 && appStore.navigation.header.job_count > 0"
                                    class="w-5 h-5 ml-2 rounded-full text-xs text-white bg-tertiary transition-colors inline-flex items-center justify-center font-bold leading-5 tracking-[.0025rem]"
                                >
                                    {{ appStore.navigation.header.job_count }}
                                </span>
                            </a>
                        </div>
                    </transition>
                    <transition-group name="fade">
                        <div key="rightlinks" v-if="showPageLinks && rightLinks" class="lg:flex items-center hidden ml-auto gap-8">
                            <a
                                v-for="page in rightLinks"
                                :href="page.url"
                                :class="[
                                    page.class,
                                    {
                                        // unstyled links
                                        'text-md transition-colors inline-flex items-center hover:text-tertiary': page.class === 'link-unstyled',
                                        '!text-tertiary': currentlyViewing(page.url) && page.class === 'link-unstyled',
                                        'text-white': firstSectionOverlap && !appStore.navigation.header.sticky && page.class === 'link-unstyled',
                                        'text-primary': (!firstSectionOverlap && !appStore.navigation.header.sticky && page.class === 'link-unstyled') || (appStore.navigation.header.sticky && page.class === 'link-unstyled'),
                                    },
                                ]"
                            >
                                {{ page.title }}
                                <svg-vue v-if="page.class.indexOf('btn') >= 0" class="w-4 h-auto ml-2" icon="icons/arrow-right"></svg-vue>
                                <span
                                    v-else-if="page.url.indexOf('jobs') >= 0 && appStore.navigation.header.job_count > 0"
                                    class="w-5 h-5 ml-2 rounded-full text-xs text-white bg-tertiary transition-colors inline-flex items-center justify-center font-bold leading-5 tracking-[.0025rem]"
                                >
                                    {{ appStore.navigation.header.job_count }}
                                </span>
                            </a>
                        </div>
                        <language-switch key="language-switch" :inverse="!firstSectionOverlap || appStore.navigation.header.sticky" class="ml-12 self-stretch lg:flex lg:items-center hidden" :translated-routes="translatedRoutes"></language-switch>
                    </transition-group>
                    <hamburger-button class="ml-auto" :dark="!firstSectionOverlap" v-if="showHamburgerButton"></hamburger-button>
                </div>
            </div>
        </header>
    </transition>
</template>

<style lang="scss" scoped>
header {
    @apply fixed z-[998] top-0 w-screen transition-all duration-300;

    &.sticky {
        box-shadow: inset 0 -1px 0 0 #d8d8d8;
        @apply bg-white;
    }

    &.slideout-visible {
        box-shadow: none;
        @apply bg-transparent;
    }
}
</style>
